import { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const PLAN_LIMITS = {
  free: {
    name: 'Free',
    websites: 1,
    products: 10,
    storage: 100, // MB
    pages: 3,
    features: ['basic_templates', 'basic_editor']
  },
  starter: {
    name: 'Starter',
    websites: 3,
    products: 50,
    storage: 500, // MB
    pages: 10,
    features: ['all_templates', 'advanced_editor', 'custom_domain', 'basic_seo']
  },
  professional: {
    name: 'Professional',
    websites: 10,
    products: 500,
    storage: 2048, // 2GB
    pages: 50,
    features: ['all_templates', 'advanced_editor', 'custom_domain', 'advanced_seo', 'analytics', 'ai_content']
  },
  enterprise: {
    name: 'Enterprise',
    websites: 999999, // Unlimited
    products: 999999, // Unlimited
    storage: 10240, // 10GB
    pages: 999999, // Unlimited
    features: ['all_features', 'priority_support', 'white_label', 'api_access', 'team_collaboration']
  }
};

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setSubscription(userData.subscription || { plan: 'free', status: 'active' });
        } else {
          setSubscription({ plan: 'free', status: 'active' });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching subscription:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const getPlanLimits = () => {
    if (!subscription) return PLAN_LIMITS.free;
    return PLAN_LIMITS[subscription.plan] || PLAN_LIMITS.free;
  };

  const canCreateWebsite = async () => {
    if (!user || !subscription) return false;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const limits = getPlanLimits();
        return (userData.websitesCount || 0) < limits.websites;
      }
      return false;
    } catch (error) {
      console.error('Error checking website limit:', error);
      return false;
    }
  };

  const canAddProduct = async () => {
    if (!user || !subscription) return false;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const limits = getPlanLimits();
        return (userData.productsCount || 0) < limits.products;
      }
      return false;
    } catch (error) {
      console.error('Error checking product limit:', error);
      return false;
    }
  };

  const hasFeature = (feature) => {
    if (!subscription) return false;
    const limits = getPlanLimits();
    return limits.features.includes(feature);
  };

  const isPlanActive = () => {
    if (!subscription) return true; // Free plan is always active
    return subscription.status === 'active';
  };

  const getPlanName = () => {
    if (!subscription) return 'Free';
    return PLAN_LIMITS[subscription.plan]?.name || 'Free';
  };

  return {
    subscription,
    loading,
    error,
    planLimits: getPlanLimits(),
    canCreateWebsite,
    canAddProduct,
    hasFeature,
    isPlanActive,
    getPlanName,
    PLAN_LIMITS
  };
};

export default useSubscription;
